import { useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { SuperAdminContext } from 'src/components';
import { useSuperAdminApiOverrides } from 'src/hooks/superAdmin';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { useMagicFlowDebuggerController } from './useMagicFlowDebuggerController';
import { useMagicFlowRulesetController } from './useMagicFlowRulesetController';
import { useMagicFlowTemplatesController } from './useMagicFlowTemplatesController';

export const useMagicFlowAdminController = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const { org } = useContext(SuperAdminContext);
  const orgUuid = org?.org_uuid;
  const hasOrgSelected = Boolean(orgUuid);
  const { headers: superAdminHeaders } = useSuperAdminApiOverrides();

  const scopedHeaders = useMemo(() => {
    return {
      ...(superAdminHeaders || {}),
    } as Record<string, string | boolean>;
  }, [superAdminHeaders]);

  const schemaHeaders = useMemo(() => {
    const nextHeaders = {
      ...(superAdminHeaders || {}),
    } as Record<string, string | boolean>;
    delete nextHeaders['Impersonate-Org-UUID'];
    delete nextHeaders['Headless-Mode'];
    return nextHeaders;
  }, [superAdminHeaders]);

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname === '/superadmin/magicflow') {
      navigate('/superadmin/magicflow/ruleset', { replace: true });
    }
  }, [location.pathname, navigate]);

  const templatesController = useMagicFlowTemplatesController({
    orgUuid,
    hasOrgSelected,
    scopedHeaders,
  });

  const rulesetController = useMagicFlowRulesetController({
    orgUuid,
    hasOrgSelected,
    scopedHeaders,
    schemaHeaders,
    templateOptions: templatesController.templateOptions,
    templateOverrideOptions: templatesController.templateOverrideOptions,
    priorityOptions: templatesController.priorityOptions,
  });

  const debuggerController = useMagicFlowDebuggerController({
    orgUuid,
    hasOrgSelected,
    scopedHeaders,
  });

  const activeTab = location.pathname.includes(
    '/superadmin/magicflow/templates'
  )
    ? 'workflow-templates'
    : location.pathname.includes('/superadmin/magicflow/debugger')
      ? 'debugger'
      : 'ruleset';

  return {
    activeTab,
    hasOrgSelected,
    onTabChange: (key: string) =>
      navigate(
        key === 'workflow-templates'
          ? '/superadmin/magicflow/templates'
          : key === 'debugger'
            ? '/superadmin/magicflow/debugger'
            : '/superadmin/magicflow/ruleset'
      ),
    openCreateTemplate: () => navigate('/superadmin/magicflow/templates/new'),
    ...templatesController,
    ...rulesetController,
    ...debuggerController,
  };
};
